/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useRef } from 'react';
import Media from 'react-media';

import ImgixWithPrioritizedLoading from 'js/components/ImgixWithPrioritizedLoading';

import { Grid, Typography, Typography2, breakpoints } from '@coursera/cds-core';

import useUserAgent from 'bundles/page/hooks/useUserAgent';
import HeroBadges from 'bundles/unified-description-page-common/components/modules/hero/HeroBadges';
import type { Props as HeroBadgesProps } from 'bundles/unified-description-page-common/components/modules/hero/HeroBadges';
import useUpdateCtaPosition from 'bundles/unified-description-page-common/hooks/useUpdateCtaPosition';
import { useXDPRedesignPhase1Data } from 'bundles/unified-description-page-common/providers/specialization/XDPRedesignPhase1Provider';

import DefaultImage from 'images/unified-description-page/default-hero-image.png';

type Props = {
  productTitle: string;
  partnerLogo?: React.ReactNode;
  description?: React.ReactNode;
  primaryCta?: React.ReactNode;
  secondaryCta?: React.ReactNode;
  additionalInfo?: React.ReactNode;
  secondaryInfo?: React.ReactNode;
  instructors?: React.ReactNode;
  additionalCta?: React.ReactNode;
  showBackgroundImage?: boolean;
  backgroundImage?: string;
  heroBadgesProps?: HeroBadgesProps;
};

const originalStyles = {
  container: css`
    padding: var(--cds-spacing-600) 0 88px;
    position: relative;

    ${breakpoints.down('sm')} {
      padding: var(--cds-spacing-400) 0 var(--cds-spacing-300);
    }
  `,
  backgroundImage: css`
    position: absolute;
    right: 0;
    bottom: calc(var(--cds-spacing-600) * -1);
  `,
  title: css`
    /* stylelint-disable-next-line no-empty-source */
  `,
  content: css`
    position: relative;
  `,
  logo: css`
    margin-bottom: var(--cds-spacing-200);
    ${breakpoints.down('sm')} {
      margin-top: 0;
      margin-bottom: var(--cds-spacing-150);
    }
  `,
  instructors: css`
    margin-top: var(--cds-spacing-50);
  `,
  description: css`
    margin-top: var(--cds-spacing-50);
  `,
  ctas: css`
    display: flex;
    align-items: baseline;

    ${breakpoints.down('sm')} {
      flex-direction: column;
      align-items: normal;
      gap: var(--cds-spacing-200);

      button,
      a,
      form {
        width: 100% !important;
        max-width: 100%;
      }
    }
  `,
  // This has the unfortunate job of adjusting styling for consumer and enterprise enroll buttons.
  // We import these buttons from other bundles so don't have direct control over styling.
  primaryCta: css`
    margin-top: var(--cds-spacing-400);

    button,
    a,
    form {
      width: auto !important;
    }

    form button {
      padding-left: var(--cds-spacing-1000);
      padding-right: var(--cds-spacing-1000);
    }

    ${breakpoints.down('sm')} {
      button,
      a,
      form {
        width: 100% !important;
        max-width: 100%;
      }
    }
  `,
  additionalCta: css`
    margin-top: var(--cds-spacing-100);
  `,
  additionalInfo: css`
    margin-top: var(--cds-spacing-150);
  `,
};

const redesignStyles = {
  ...originalStyles,
  container: css`
    position: relative;
    padding: 0;
  `,
  logo: css`
    padding-top: var(--cds-spacing-50);
    margin-bottom: 0;
    ${breakpoints.down('sm')} {
      margin-top: 0;
      margin-bottom: 0;
    }
  `,
  title: css`
    margin-bottom: var(--cds-spacing-100);
  `,
  description: css`
    margin-bottom: var(--cds-spacing-100);
  `,
  backgroundImage: css`
    position: absolute;
    right: -106px;
    bottom: -64px;
    pointer-events: none;
  `,
  ctas: css`
    margin-top: var(--cds-spacing-300);

    ${breakpoints.down('sm')} {
      margin-top: var(--cds-spacing-200);
    }
  `,
  primaryCta: css`
    margin-top: 0;

    button,
    a,
    form {
      width: auto !important;
    }

    form button {
      padding-left: var(--cds-spacing-1000);
      padding-right: var(--cds-spacing-1000);
    }

    ${breakpoints.down('xs')} {
      button,
      a,
      form {
        width: 100% !important;
        max-width: 100%;
      }
    }
  `,
};

const HeroModuleWithImage = ({
  productTitle,
  partnerLogo,
  description,
  primaryCta,
  secondaryCta,
  additionalInfo,
  secondaryInfo,
  instructors,
  additionalCta,
  showBackgroundImage,
  backgroundImage,
  heroBadgesProps,
}: Props) => {
  const userAgent = useUserAgent();
  const pageCtaRef = useRef<HTMLDivElement | null>(null);
  useUpdateCtaPosition(pageCtaRef);

  const { enableXDPRedesignPhase1 } = useXDPRedesignPhase1Data();
  const styles = enableXDPRedesignPhase1 ? redesignStyles : originalStyles;

  const imgParams = {
    quality: 80,
  };

  const getTitle = () => {
    if (enableXDPRedesignPhase1) {
      return (
        <Typography2 data-e2e="hero-title" variant="titleLargeCompact" component="h1" css={styles.title}>
          {productTitle}
        </Typography2>
      );
    }

    return (
      <Typography data-e2e="hero-title" variant="d2semibold" component="h1" css={styles.title}>
        {productTitle}
      </Typography>
    );
  };

  return (
    <Media query={{ maxWidth: breakpoints.values.md - 1 }} defaultMatches={userAgent?.isMobileBrowser}>
      {(matches) => (
        <div css={styles.container}>
          {matches && <HeroBadges {...heroBadgesProps} />}
          {showBackgroundImage && !matches && (
            <ImgixWithPrioritizedLoading
              src={backgroundImage ?? DefaultImage}
              alt=""
              width={566}
              imgParams={imgParams}
              css={styles.backgroundImage}
            />
          )}
          <Grid container data-e2e="hero-module" spacing={16} css={styles.content}>
            <Grid item xs={12}>
              {partnerLogo && <div css={styles.logo}>{partnerLogo}</div>}
            </Grid>

            <React.Fragment>
              <Grid item xs={12} md={matches ? 12 : enableXDPRedesignPhase1 ? 8 : 7}>
                {getTitle()}
                {description && <div css={styles.description}>{description}</div>}
                {!enableXDPRedesignPhase1 && instructors && <div css={styles.instructors}>{instructors}</div>}
                {primaryCta && (
                  <div css={styles.ctas}>
                    <div css={styles.primaryCta} ref={pageCtaRef}>
                      {primaryCta}
                    </div>
                    {secondaryCta}
                  </div>
                )}
                {additionalInfo && <div css={styles.additionalInfo}>{additionalInfo}</div>}
                {additionalCta && <div css={styles.additionalCta}>{additionalCta}</div>}
              </Grid>
              {!matches && secondaryInfo && (
                <Grid item md={5}>
                  {secondaryInfo}
                </Grid>
              )}
            </React.Fragment>
          </Grid>
        </div>
      )}
    </Media>
  );
};

export default HeroModuleWithImage;
